import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import api from '@/api/axios';
import { Button } from '@/components/ui/button';
import { Note as NoteIcon, X, ArrowSquareOut, Plus, CheckCircle, Circle, Calendar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { NoteEditor } from '@/pages/Pendientes';

const todayISO = () => new Date().toISOString().slice(0, 10);

const FloatingNotes = ({ hideOnPaths = [] }) => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [creating, setCreating] = useState(false);
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/notes', { params: { scope: 'all', show_completed: false } });
      const arr = (data || []).slice(0, 10);
      setNotes(arr);
      setCount((data || []).filter(n => !n.completed).length);
    } catch (e) { /* silent */ }
  }, []);

  useEffect(() => {
    load();
    const intv = setInterval(load, 60000);
    return () => clearInterval(intv);
  }, [load]);

  const overdueCount = notes.filter(n => n.due_date && n.due_date < todayISO() && !n.completed).length;

  // Hide on certain routes (e.g. /chat where it would overlap with the input)
  if (hideOnPaths.some(p => location.pathname.startsWith(p))) return null;

  const handleSave = async (draft) => {
    try {
      await api.post('/notes', draft);
      toast.success('Pendiente creado');
      setCreating(false);
      load();
    } catch (e) {
      toast.error('Error al guardar');
    }
  };

  const handleToggle = async (n) => {
    try {
      await api.patch(`/notes/${n.id}/toggle`);
      load();
    } catch (e) { /* silent */ }
  };

  return (
    <>
      <button
        onClick={() => setOpen(o => !o)}
        className="fixed bottom-4 right-4 z-50 w-12 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center transition-all"
        data-testid="floating-notes-toggle"
        title="Pendientes"
      >
        <NoteIcon size={22} weight="duotone" />
        {count > 0 && (
          <span className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ${overdueCount > 0 ? 'bg-red-500 text-white' : 'bg-amber-500 text-white'}`}>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          className="fixed bottom-20 right-4 z-50 w-[340px] max-w-[calc(100vw-2rem)] max-h-[70vh] bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="floating-notes-panel"
        >
          <div className="flex items-center justify-between p-3 border-b border-[var(--border)] bg-blue-600 text-white">
            <h3 className="text-sm font-black flex items-center gap-1">
              <NoteIcon size={16} weight="fill" /> Pendientes
            </h3>
            <div className="flex items-center gap-1">
              <Link to="/pendientes" onClick={() => setOpen(false)} title="Abrir módulo">
                <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-white hover:bg-white/20" data-testid="open-pendientes-page">
                  <ArrowSquareOut size={14} />
                </Button>
              </Link>
              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-white hover:bg-white/20" onClick={() => setOpen(false)}>
                <X size={14} />
              </Button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {creating ? (
              <NoteEditor
                defaultScope="private"
                onSave={handleSave}
                onCancel={() => setCreating(false)}
              />
            ) : (
              <Button onClick={() => setCreating(true)} className="w-full bg-blue-600 hover:bg-blue-700 text-white" size="sm" data-testid="floating-add-btn">
                <Plus size={14} /> Nuevo pendiente
              </Button>
            )}
            {notes.length === 0 ? (
              <p className="text-center text-xs text-[var(--text-muted)] py-6">No tienes pendientes. ¡Bien hecho!</p>
            ) : (
              notes.map(n => {
                const overdue = n.due_date && n.due_date < todayISO();
                return (
                  <div key={n.id} className="flex items-start gap-2 p-2 rounded border border-[var(--border)] bg-[var(--surface-muted)]">
                    <button onClick={() => handleToggle(n)} className="mt-0.5 text-[var(--text-muted)] hover:text-emerald-600">
                      {n.completed ? <CheckCircle size={16} weight="fill" className="text-emerald-600" /> : <Circle size={16} />}
                    </button>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold text-[var(--text)] truncate" title={n.title}>{n.title}</div>
                      {n.due_date && (
                        <div className={`text-[10px] inline-flex items-center gap-0.5 ${overdue ? 'text-red-600 font-bold' : 'text-[var(--text-muted)]'}`}>
                          <Calendar size={10} /> {n.due_date}{overdue && ' (vencida)'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingNotes;
